import {
  ACTIVITY_PANEL_MAX_ITEMS,
  FEATURED_PANEL_MAX_ITEMS,
  IMonthlyPulseBadgeItem,
  IMonthlyPulseLeaderboardItem,
  IMonthlyPulseMediaItem,
} from 'fitbeat.models';
import { IMonthlyPulseData, MonthlyPulseUnit, toPulseValue } from './MonthlyPulseData';
import { IMonthlyPulseVariables } from './MonthlyPulseVariables';

export const toMonthlyPulseVariables = (data: IMonthlyPulseData): IMonthlyPulseVariables => {
  const variables: Partial<IMonthlyPulseVariables> = {
    companyName: data.meta.companyName,
    companyLogoUrl: data.meta.companyLogoUrl ?? '',
    selectedMonth: data.meta.selectedMonth,
    heading: data.meta.heading,
    fitillionLogoUrl: data.meta.fitillionLogoUrl,
    generatedAt: data.meta.generatedAt,

    congratulationsSentToDate: data.social.congratulationsSent.toDate,
    congratulationsSentToDateSuffix: data.social.congratulationsSent.toDateSuffix,
    congratulationsSentThisMonth: data.social.congratulationsSent.thisMonth,
    congratulationsSentThisMonthSuffix: data.social.congratulationsSent.thisMonthSuffix,
    challengesCompletedToDate: data.social.challengesCompleted.toDate,
    challengesCompletedToDateSuffix: data.social.challengesCompleted.toDateSuffix,
    challengesCompletedThisMonth: data.social.challengesCompleted.thisMonth,
    challengesCompletedThisMonthSuffix: data.social.challengesCompleted.thisMonthSuffix,
    interactionsCountToDate: data.social.interactionsCount.toDate,
    interactionsCountToDateSuffix: data.social.interactionsCount.toDateSuffix,
    interactionsCountThisMonth: data.social.interactionsCount.thisMonth,
    interactionsCountThisMonthSuffix: data.social.interactionsCount.thisMonthSuffix,

    activityGroupMediaTitle: data.social.activityGroupMedia.title,
    activityGroupMediaSubtitle: data.social.activityGroupMedia.subtitle,
    activityGroupMediaImageUrl: data.social.activityGroupMedia.imageUrl,

    interestGroupMediaTitle: data.social.interestGroupMedia.title,
    interestGroupMediaSubtitle: data.social.interestGroupMedia.subtitle,
    interestGroupMediaImageUrl: data.social.interestGroupMedia.imageUrl,

    readsThisYear: data.wellness.readsThisYear,
    readsThisYearSuffix: data.wellness.readsThisYearSuffix,
    blogsReadThisMonth: data.wellness.readsThisMonth,
    blogsReadThisMonthSuffix: data.wellness.readsThisMonthSuffix,
    listensThisYear: data.wellness.listensThisYear,
    listensThisYearSuffix: data.wellness.listensThisYearSuffix,
    podcastsListenedThisMonth: data.wellness.listensThisMonth,
    podcastsListenedThisMonthSuffix: data.wellness.listensThisMonthSuffix,

    experiencePointsToDate: data.achievements.experiencePointsToDate,
    experiencePointsToDateSuffix: data.achievements.experiencePointsToDateSuffix,
    experiencePointsThisMonth: data.achievements.experiencePointsThisMonth,
    experiencePointsThisMonthSuffix: data.achievements.experiencePointsThisMonthSuffix,
    achievementsToDate: data.achievements.achievementsToDate,
    achievementsToDateSuffix: data.achievements.achievementsToDateSuffix,
    achievementsThisMonth: data.achievements.achievementsThisMonth,
    achievementsThisMonthSuffix: data.achievements.achievementsThisMonthSuffix,
    featuredChallengeJoinPercent: data.achievements.featuredChallengeJoinPercent,
    featuredChallengeJoinPercentSuffix: data.achievements.featuredChallengeJoinPercentSuffix,
    featuredChallengeJoinVisible: data.achievements.featuredChallengeJoinVisible ? 'true' : 'false',
    weeklyActivityGoalsPercent: data.achievements.weeklyActivityGoalsPercent,
    weeklyActivityGoalsPercentSuffix: data.achievements.weeklyActivityGoalsPercentSuffix,
    weeklyActivityGoalsTotal: data.achievements.weeklyActivityGoalsTotal,
    weeklyActivityGoalsTotalSuffix: data.achievements.weeklyActivityGoalsTotalSuffix,
    dailyStepsGoalsPercent: data.achievements.dailyStepsGoalsPercent,
    dailyStepsGoalsPercentSuffix: data.achievements.dailyStepsGoalsPercentSuffix,
    dailyStepsGoalsTotal: data.achievements.dailyStepsGoalsTotal,
    dailyStepsGoalsTotalSuffix: data.achievements.dailyStepsGoalsTotalSuffix,

    featuredLeaderboardType: data.leaderboards.featured.type,
    featuredLeaderboardName: data.leaderboards.featured.leaderboardName,

    stepsThisYear: data.exercise.stepsThisYear,
    stepsThisYearSuffix: data.exercise.stepsThisYearSuffix,
    stepsThisMonth: data.exercise.stepsThisMonth,
    stepsThisMonthSuffix: data.exercise.stepsThisMonthSuffix,
    aroundTheEarthPercent: data.exercise.aroundTheEarthPercent,
    aroundTheEarthPercentSuffix: data.exercise.aroundTheEarthPercentSuffix,
    activeMinutesToDate: data.exercise.activeMinutesToDate,
    activeMinutesToDateSuffix: data.exercise.activeMinutesToDateSuffix,
    activeMinutesThisMonth: data.exercise.activeMinutesThisMonth,
    activeMinutesThisMonthSuffix: data.exercise.activeMinutesThisMonthSuffix,
    daysOfMovement: data.exercise.daysOfMovement,
    daysOfMovementSuffix: data.exercise.daysOfMovementSuffix,
    workoutsToDate: data.exercise.workoutsToDate,
    workoutsToDateSuffix: data.exercise.workoutsToDateSuffix,
    workoutsThisMonth: data.exercise.workoutsThisMonth,
    workoutsThisMonthSuffix: data.exercise.workoutsThisMonthSuffix,
    caloriesBurntToDate: data.exercise.caloriesBurntToDate,
    caloriesBurntToDateSuffix: data.exercise.caloriesBurntToDateSuffix,
    caloriesBurntThisMonth: data.exercise.caloriesBurntThisMonth,
    caloriesBurntThisMonthSuffix: data.exercise.caloriesBurntThisMonthSuffix,
    caloriesBurntFoodEquivalentToDate: data.exercise.caloriesBurntFoodEquivalentToDate,
    caloriesBurntFoodEquivalentToDateSuffix: data.exercise.caloriesBurntFoodEquivalentToDateSuffix,
    caloriesBurntFoodEquivalentThisMonth: data.exercise.caloriesBurntFoodEquivalentThisMonth,
    caloriesBurntFoodEquivalentThisMonthSuffix: data.exercise.caloriesBurntFoodEquivalentThisMonthSuffix,
    caloriesConsumedToDate: data.exercise.caloriesConsumedToDate,
    caloriesConsumedToDateSuffix: data.exercise.caloriesConsumedToDateSuffix,
    caloriesConsumedBurgerEquivalentToDate: data.exercise.caloriesConsumedBurgerEquivalentToDate,
    caloriesConsumedBurgerEquivalentToDateSuffix: data.exercise.caloriesConsumedBurgerEquivalentToDateSuffix,
    caloriesConsumedThisMonth: data.exercise.caloriesConsumedThisMonth,
    caloriesConsumedThisMonthSuffix: data.exercise.caloriesConsumedThisMonthSuffix,
    caloriesConsumedBurgerEquivalentThisMonth: data.exercise.caloriesConsumedBurgerEquivalentThisMonth,
    caloriesConsumedBurgerEquivalentThisMonthSuffix: data.exercise.caloriesConsumedBurgerEquivalentThisMonthSuffix,
    strengthWorkoutsThisMonth: data.exercise.workoutsByType.strengthThisMonth,
    strengthWorkoutsThisMonthSuffix: data.exercise.workoutsByType.strengthThisMonthSuffix,
    cardioWorkoutsThisMonth: data.exercise.workoutsByType.cardioThisMonth,
    cardioWorkoutsThisMonthSuffix: data.exercise.workoutsByType.cardioThisMonthSuffix,
    mobilityWorkoutsThisMonth: data.exercise.workoutsByType.mobilityThisMonth,
    mobilityWorkoutsThisMonthSuffix: data.exercise.workoutsByType.mobilityThisMonthSuffix,
    workoutsByTypeTitle: data.exercise.workoutsByTypeTitle,
    // Percentages come from raw monthly counts in aggregates — do not recompute
    // from display-scaled values (1.5K vs 900 would skew donut segments).
    donutStrengthPct: data.exercise.workoutsByType.donutStrengthPct,
    donutCardioPct: data.exercise.workoutsByType.donutCardioPct,
    donutMobilityPct: data.exercise.workoutsByType.donutMobilityPct,

    nutritionPopularBlogTitle: data.nutrition.popularBlogMedia.title,
    nutritionPopularBlogSubtitle: data.nutrition.popularBlogMedia.subtitle,
    nutritionPopularBlogImageUrl: data.nutrition.popularBlogMedia.imageUrl,
    nutritionPopularPodcastTitle: data.nutrition.popularPodcastMedia.title,
    nutritionPopularPodcastSubtitle: data.nutrition.popularPodcastMedia.subtitle,
    nutritionPopularPodcastImageUrl: data.nutrition.popularPodcastMedia.imageUrl,
  };

  for (let i = 0; i < 2; i += 1) {
    Object.assign(
      variables,
      toMediaItemVariables('wellnessSpotlightItem', data.wellness.mediaSpotlight[i], i + 1),
    );
  }

  for (let i = 0; i < 4; i += 1) {
    Object.assign(
      variables,
      toBadgeItemVariables('badge', data.achievements.badges[i], i + 1),
    );
  }

  for (let i = 0; i < FEATURED_PANEL_MAX_ITEMS; i += 1) {
    Object.assign(
      variables,
      toLeaderboardItemVariables('featuredLeaderboardItem', data.leaderboards.featured.items[i], i + 1),
    );
  }

  for (let i = 0; i < ACTIVITY_PANEL_MAX_ITEMS; i += 1) {
    Object.assign(
      variables,
      toLeaderboardItemVariables('activityLeaderboardItem', data.leaderboards.activityGroups.items[i], i + 1),
    );
  }

  for (let i = 0; i < 3; i += 1) {
    Object.assign(
      variables,
      toMediaItemVariables('nutritionMeal', data.nutrition.popularMealsMedia[i], i + 1),
    );
  }

  return variables as IMonthlyPulseVariables;
};

const toLeaderboardItemVariables = (
  prefix: string,
  item: IMonthlyPulseLeaderboardItem | undefined,
  index: number,
): Partial<IMonthlyPulseVariables> => {
  const rankKey = `${prefix}${index}Rank`;
  const rankSuffixKey = `${prefix}${index}RankSuffix`;
  const nameKey = `${prefix}${index}Name`;
  const scoreKey = `${prefix}${index}Score`;
  const scoreSuffixKey = `${prefix}${index}ScoreSuffix`;
  const imageUrlKey = `${prefix}${index}ImageUrl`;
  const leaderboardNameKey = `${prefix}${index}LeaderboardName`;
  const primaryColourKey = `${prefix}${index}PrimaryColour`;

  if (item) {
    const rank = toPulseValue(item.rank);
    // null score = activity group with no winner; show a dash in the name cell.
    const score = item.score === null
      ? { value: '', suffix: '' as MonthlyPulseUnit }
      : toPulseValue(item.score);
    return {
      [rankKey]: rank.value,
      [rankSuffixKey]: rank.suffix,
      [nameKey]: item.score === null ? '-' : item.name,
      [scoreKey]: score.value,
      [scoreSuffixKey]: score.suffix,
      [imageUrlKey]: item.imageUrl ?? '',
      [leaderboardNameKey]: item.leaderboardName,
      [primaryColourKey]: item.primaryColour ?? '',
    };
  }
  const zero = toPulseValue(0);
  return {
    [rankKey]: zero.value,
    [rankSuffixKey]: zero.suffix,
    [nameKey]: '',
    [scoreKey]: zero.value,
    [scoreSuffixKey]: zero.suffix,
    [imageUrlKey]: '',
    [leaderboardNameKey]: '',
    [primaryColourKey]: '',
  };
};

const toMediaItemVariables = (
  prefix: string,
  item: IMonthlyPulseMediaItem | undefined,
  index: number,
): Partial<IMonthlyPulseVariables> => {
  const titleKey = `${prefix}${index}Title`;
  const subtitleKey = `${prefix}${index}Subtitle`;
  const imageUrlKey = `${prefix}${index}ImageUrl`;

  if (item) {
    return {
      [titleKey]: item.title,
      [subtitleKey]: item.subtitle,
      [imageUrlKey]: item.imageUrl ?? '',
    };
  }
  return {
    [titleKey]: '',
    [subtitleKey]: '',
    [imageUrlKey]: '',
  };
};

const toBadgeItemVariables = (
  prefix: string,
  item: IMonthlyPulseBadgeItem | undefined,
  index: number,
): Partial<IMonthlyPulseVariables> => {
  const nameKey = `${prefix}${index}UserName`;
  const badgeNameKey = `${prefix}${index}AchievementName`;
  const badgeUrlKey = `${prefix}${index}IconUrl`;

  if (item) {
    return {
      [nameKey]: item.name,
      [badgeNameKey]: item.badgeName,
      [badgeUrlKey]: item.badgeUrl ?? '',
    };
  }
  return {
    [nameKey]: '',
    [badgeNameKey]: '',
    [badgeUrlKey]: '',
  };
};
